import { countryForTimeZone, TIME_ZONE_COUNTRIES } from './utils/timeZones';

/**
 * The country the shopper is in.
 *
 * A market is just a country code; its language and currency are derived from
 * it, so any country works without an entry here. It picks the UI language and
 * the currency a purchase form starts on. Shared by the functions and the
 * Angular frontend so both agree on what a valid market is.
 */

/** ISO 3166-1 alpha-2, lower case: 'bg', 'es', 'de'… */
export type MarketId = string;

export interface Market {
   id: MarketId;
   /** The country's main language (ISO 639-1), from CLDR likely subtags: es → es, ch → de, be → nl. */
   language: string;
}

/**
 * Where everything written before markets existed belongs, and what an
 * unknown or missing market falls back to.
 */
export const DEFAULT_MARKET_ID: MarketId = 'bg';

const MARKET_ID_SET = new Set(TIME_ZONE_COUNTRIES.map((country) => country.toLowerCase()));

/** Every valid market id: the countries that have a time zone. */
export const MARKET_IDS: readonly MarketId[] = [...MARKET_ID_SET];

export function isMarketId(value: unknown): value is MarketId {
   return typeof value === 'string' && MARKET_ID_SET.has(value);
}

function languageOf(id: MarketId): string {
   try {
      return new Intl.Locale(`und-${id.toUpperCase()}`).maximize().language;
   } catch {
      return 'en';
   }
}

/**
 * The market for an untrusted value — a request field, a stored document, a
 * localStorage entry. Anything that isn't a country code is the default, so a
 * crafted request can't send provider calls anywhere odd, and a document
 * written before markets existed reads as the market it was.
 */
export function resolveMarket(value: unknown): Market {
   const id = isMarketId(value) ? value : DEFAULT_MARKET_ID;
   const language = languageOf(id);
   return { id, language };
}

/**
 * The market a time zone lies in, or undefined. The time zone is what the
 * device's clock is set to, and phones and laptops switch it automatically on
 * arrival, so it follows the shopper across borders without a permission
 * prompt or a network call. The browser language would not: a Bulgarian in
 * Madrid still reads `bg`.
 */
export function marketForTimeZone(timeZone: string | undefined): MarketId | undefined {
   return timeZone ? countryForTimeZone(timeZone)?.toLowerCase() : undefined;
}

/** A country's name in `displayLanguage` ("Испания", "Spain"), or the code when unknown. */
export function marketName(id: MarketId, displayLanguage: string): string {
   try {
      return new Intl.DisplayNames([displayLanguage], { type: 'region' }).of(id.toUpperCase()) ?? id;
   } catch {
      return id.toUpperCase();
   }
}

/** A language's name in `displayLanguage` ("испански"), or the code when unknown. */
export function languageName(language: string, displayLanguage: string): string {
   try {
      return new Intl.DisplayNames([displayLanguage], { type: 'language' }).of(language) ?? language;
   } catch {
      return language;
   }
}

/** Euro area and the countries that use the euro without being in it. Bulgaria joined on 2026-01-01. */
const EURO_COUNTRIES = new Set([
   'at', 'be', 'bg', 'cy', 'de', 'ee', 'es', 'fi', 'fr', 'gr', 'hr', 'ie', 'it', 'lt', 'lu', 'lv', 'mt', 'nl', 'pt',
   'si', 'sk', 'ad', 'mc', 'sm', 'va', 'me', 'xk',
]);

/** Non-euro currencies of the countries shoppers here are likely to be in. */
const OTHER_CURRENCIES: Record<MarketId, string> = {
   ro: 'RON', pl: 'PLN', cz: 'CZK', hu: 'HUF', se: 'SEK', dk: 'DKK', no: 'NOK', is: 'ISK', ch: 'CHF', li: 'CHF',
   gb: 'GBP', us: 'USD', ca: 'CAD', au: 'AUD', tr: 'TRY', rs: 'RSD', mk: 'MKD', al: 'ALL', ba: 'BAM', md: 'MDL',
   ua: 'UAH',
};

/**
 * The currency a shopper in this country most likely paid in (ISO 4217). A
 * default for a form, never a fact about a price: anywhere not listed reads as
 * euro, which the user can change.
 */
export function currencyOfMarket(id: MarketId): string {
   return EURO_COUNTRIES.has(id) ? 'EUR' : OTHER_CURRENCIES[id] ?? 'EUR';
}
